import React, {useEffect, useState } from 'react'
import { Select, Tooltip } from 'antd'
import { QuestionCircleIcon } from 'components/Icons'
import { useEntityData } from 'EntityDataContext/EntityDataContext'
import {
  DEFAULT_LIFECYCLE,
  CONSEQUENCE_OF_FAILURE_OPTIONS,
  LIKELIHOOD_OF_FAILURE_TITLE_MAP,
  LIKELIHOOD_OF_FAILURE_MESSAGE_MAP,
  RISK_LABEL_MAP,
  RISK_INDICATOR_CLASS_MAP,
  HELP_TEXT
} from './helpers'
import LikelihoodConsequenceRiskStyled from './LikelihoodConsequenceRiskStyled'

const LikelihoodConsequenceRiskField = ({
  inputKey = "likelihood-consequence-risk",
  form = {}
}) => {
  const { tenantData: { projectReportingThreshold } = {} } = useEntityData()

  const assetType = form.getFieldValue('assetType') || undefined
  const installDate = form.getFieldValue('facets_install-date') ? new Date(form.getFieldValue('facets_install-date')).getFullYear() : undefined
  const liy = form.getFieldValue('facets_lifecycle-intervention-year') ? new Date(form.getFieldValue('facets_lifecycle-intervention-year')).getFullYear() : undefined // Lifecycle Intervention Year
  const lcrScores = form.getFieldValue('facets_likelihood-consequence-risk') || undefined
  const lcrLikelihood = lcrScores ? parseInt(lcrScores.split('-')[0]) : 0 // Likelihood Score
  const lcrConsequence = lcrScores ? parseInt(lcrScores.split('-')[1]) : 0 // Consequence Score
  const lcrRisk = lcrScores ? parseInt(lcrScores.split('-')[2]) : 0 // Risk Score

  const [showFacetQuestions, setShowFacetQuestions] = useState(true)
  const [likelihoodScore, setLikelihoodScore] = useState(lcrLikelihood > 0 ? lcrLikelihood : undefined)
  const [consequenceScore, setConsequenceScore] = useState(lcrConsequence > 0 ? lcrConsequence : undefined)
  const [riskScore, setRiskScore] = useState(lcrRisk > 0 ? lcrRisk : undefined)
  const [remainingLife, setRemainingLife] = useState(undefined)

  useEffect(() => {
    if (installDate) {
      calculateRemainingLife()
    } else {
      setRemainingLife(undefined)
      setLikelihoodScore(undefined)
      setRiskScore(undefined)
      setShowFacetQuestions(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installDate, liy])

  useEffect(() => {
    if (projectReportingThreshold) {
      if (remainingLife && remainingLife > projectReportingThreshold) {
        setShowFacetQuestions(false)

        return
      }
    }

    if (remainingLife) {
      let likelihoodOfFailureLevel

      if (remainingLife >= 11) {
        likelihoodOfFailureLevel = 1
      } else if (remainingLife >= 6 && remainingLife <= 10) {
        likelihoodOfFailureLevel = 2
      } else if (remainingLife >= 2 && remainingLife <= 5) {
        likelihoodOfFailureLevel = 3
      } else if (remainingLife === 1) {
        likelihoodOfFailureLevel = 4
      } else {
        likelihoodOfFailureLevel = 5
      }

      setLikelihoodScore(likelihoodOfFailureLevel)
      setShowFacetQuestions(true)
    }
  }, [remainingLife, projectReportingThreshold])

  useEffect(() => {
    updateFormValues()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFacetQuestions, likelihoodScore, consequenceScore])

  function updateFormValues() {
    let validator = []

    if (!showFacetQuestions) {
      validator = [0, 0, 0] // default values for when no scoring is required
    } else {
      validator.push(likelihoodScore)
      validator.push(consequenceScore)

      likelihoodScore && consequenceScore ? validator.push(calculateRisk()) : validator.push(undefined)
    }

    const isValid = validator.every(v => v !== undefined)

    if (isValid) {
      form.setFieldsValue({[`${inputKey}`]: validator.join('-')})
    } else {
      form.setFieldsValue({[`${inputKey}`]: undefined}) // this will trigger the forms validation and show the component as invalid 
    }
  }

  function calculateRisk() {
    const risk = likelihoodScore * consequenceScore

    setRiskScore(risk)

    return risk
  }

  function calculateRemainingLife() {
    if (installDate) {
      const currentYear = new Date().getFullYear()
      const installedYear = new Date(installDate).getFullYear()
      const cibseEOL = installedYear + assetType?.lifecycle || DEFAULT_LIFECYCLE

      let yearsRemainingLife = cibseEOL - currentYear

      if (liy) {
        yearsRemainingLife = liy - currentYear
      }

      setRemainingLife(yearsRemainingLife)
    }
  }

  function getRiskLevel() {
    let level

    if (riskScore > 0 && riskScore <= 6) {
      level = 1
    } else if (riskScore >= 7 && riskScore <= 10) {
      level = 2
    } else if (riskScore >= 11 && riskScore <= 16) {
      level = 3
    } else if (riskScore >= 17 && riskScore <= 25) {
      level = 4
    } else {
      level = 0
    }

    return level
  }

  function handleConsequenceOfFailureChange(value) {
    setConsequenceScore(value)
  }

  return (
    <>
      {showFacetQuestions ? (
        <LikelihoodConsequenceRiskStyled>
          <div className="likelihood-consequence-risk__grid">
            <div className="likelihood-consequence-risk__input-container">
              <label className="likelihood-consequence-risk__label">Likelihood (of failure)</label>
              {likelihoodScore && (
                <>
                  <span>
                    <strong>{LIKELIHOOD_OF_FAILURE_TITLE_MAP[likelihoodScore]}</strong>{" "}
                    {LIKELIHOOD_OF_FAILURE_MESSAGE_MAP[likelihoodScore]}
                  </span>
                </>
              )}
            </div>
            <div className="likelihood-consequence-risk__input-container">
              <label className="likelihood-consequence-risk__label">
                <span>Consequence (of failure)</span>
                <Tooltip
                  title={HELP_TEXT}
                  placement="left"
                >
                  <div className="form-helper">{/* the div around the Icon triggers the tooltip on hover */}
                    <QuestionCircleIcon />
                  </div>
                </Tooltip>
              </label>
              <Select
                className="likelihood-consequence-risk__select"
                disabled={!installDate}
                options={CONSEQUENCE_OF_FAILURE_OPTIONS}
                placeholder="Consequence (of failure)"
                value={installDate ? consequenceScore : undefined}
                onChange={handleConsequenceOfFailureChange}
              />
            </div>
          </div>
          {riskScore && (
            <div className="likelihood-consequence-risk__risk-label">
              <div className={`likelihood-consequence-risk__risk-indicator ${RISK_INDICATOR_CLASS_MAP[getRiskLevel()]}`}></div>
              <span>{RISK_LABEL_MAP[getRiskLevel()]}</span>
            </div>
          )}
        </LikelihoodConsequenceRiskStyled>
      ) : (
        <div>Asset remaining life greater than {projectReportingThreshold} {`${projectReportingThreshold !== 1 ? 'years' : 'year'}`}, no Likelihood or Consequence scoring required.</div>
      )}
    </>
  )
}

export default LikelihoodConsequenceRiskField
